import { useState } from 'react';
import { supabase } from '@shared/lib/supabase';
import { getEventConfig } from './webhookEventRegistry';
import { getColorClasses } from './eventColors';

const API_BASE = import.meta.env.VITE_API_URL || '';

export interface WebhookEvent {
  id: string;
  event_id: string;
  event_type: string;
  payload: any;
  processed: boolean;
  handler_result: { action?: string; details?: any } | null;
  processing_error: string | null;
  received_at: string;
  processed_at: string | null;
}

interface Props {
  event: WebhookEvent;
  apiKey: string;
  onClose: () => void;
  onReplayed: () => void;
}

export default function SquareWebhookEventDetail({ event, apiKey, onClose, onReplayed }: Props) {
  const [replaying, setReplaying] = useState(false);
  const [replayMessage, setReplayMessage] = useState('');
  const [replayOk, setReplayOk] = useState<boolean | null>(null);

  const config = getEventConfig(event.event_type);
  const colors = getColorClasses(config.color);
  const object = event.payload?.data?.object;

  const handleReplay = async () => {
    setReplaying(true);
    setReplayMessage('');
    setReplayOk(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(
        `${API_BASE}/api/webhooks/square/events/${encodeURIComponent(event.event_id)}/replay`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Bonespay-Key': apiKey,
            Authorization: session?.access_token ? `Bearer ${session.access_token}` : '',
          },
          body: JSON.stringify({}),
        }
      );

      const rawText = await res.text();
      let data: { success?: boolean; result?: { action?: string }; error?: string } = {};
      try {
        data = rawText ? JSON.parse(rawText) : {};
      } catch {
        const snippet = rawText.trim().slice(0, 140);
        throw new Error(`Replay failed (HTTP ${res.status}).${snippet ? ` Server: "${snippet}"` : ''}`);
      }

      if (res.ok && data.success) {
        setReplayOk(true);
        setReplayMessage(`Replayed: ${data.result?.action || 'success'}`);
        setTimeout(() => onReplayed(), 1200);
      } else {
        setReplayOk(false);
        setReplayMessage(data.error || `Replay failed (HTTP ${res.status})`);
      }
    } catch (err) {
      setReplayOk(false);
      setReplayMessage(err instanceof Error ? err.message : 'Replay failed');
    } finally {
      setReplaying(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.7)' }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-lg dash-scrollbar-themed"
        style={{ background: 'var(--dash-card)', border: '1px solid var(--dash-border-solid)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="sticky top-0 p-4 flex items-start justify-between"
          style={{ background: 'var(--dash-card)', borderBottom: '1px solid var(--dash-border)' }}
        >
          <div className="flex items-start gap-3">
            <div
              className={`w-10 h-10 rounded ${colors.bg} ${colors.border} border flex items-center justify-center font-mono text-base ${colors.text}`}
            >
              {config.symbol}
            </div>
            <div>
              <h2 className="font-sans text-lg font-medium" style={{ color: 'var(--dash-text-primary)' }}>
                {config.label}
              </h2>
              <p className="font-mono text-xs" style={{ color: 'var(--dash-text-muted)' }}>{event.event_type}</p>
              <p className="font-mono text-[10px] mt-0.5" style={{ color: 'var(--dash-text-faint)' }}>{config.description}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-xl transition-colors leading-none"
            style={{ color: 'var(--dash-text-muted)' }}
            onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--dash-text-primary)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--dash-text-muted)'; }}
            aria-label="Close"
          >
            {'\u00D7'}
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <MetaField label="Event ID" mono value={event.event_id} />
            <MetaField label="Received" value={new Date(event.received_at).toLocaleString()} />
            <MetaField
              label="Status"
              value={event.processed ? 'Processed' : 'Pending'}
              valueClass={event.processed ? 'text-emerald-400' : undefined}
            />
            {event.handler_result?.action && (
              <MetaField label="Action Taken" value={event.handler_result.action} />
            )}
          </div>

          {event.processing_error && (
            <div className="p-3 rounded border border-red-500/30 bg-red-500/10">
              <div className="font-mono text-[10px] uppercase tracking-wider text-red-400 mb-1">
                Processing Error
              </div>
              <pre className="font-mono text-xs text-red-300 whitespace-pre-wrap">
                {event.processing_error}
              </pre>
            </div>
          )}

          {config.renderDetails ? (
            <div>
              <SectionLabel>Details</SectionLabel>
              {config.renderDetails(object)}
            </div>
          ) : (
            <div>
              <SectionLabel>Event Data</SectionLabel>
              <pre
                className="font-mono text-[11px] p-3 rounded overflow-x-auto"
                style={{
                  color: 'var(--dash-text-secondary)',
                  background: 'var(--dash-input)',
                  border: '1px solid var(--dash-border)',
                }}
              >
                {JSON.stringify(object, null, 2)}
              </pre>
            </div>
          )}

          <details>
            <summary
              className="font-mono text-[10px] uppercase tracking-wider cursor-pointer transition-colors"
              style={{ color: 'var(--dash-text-muted)' }}
            >
              Raw Payload
            </summary>
            <pre
              className="font-mono text-[10px] p-3 rounded mt-2 overflow-x-auto"
              style={{
                color: 'var(--dash-text-muted)',
                background: 'var(--dash-input)',
                border: '1px solid var(--dash-border)',
              }}
            >
              {JSON.stringify(event.payload, null, 2)}
            </pre>
          </details>

          <div className="pt-4" style={{ borderTop: '1px solid var(--dash-border)' }}>
            <button
              onClick={handleReplay}
              disabled={replaying}
              className="font-mono text-xs rounded px-3 py-2 transition-colors disabled:opacity-50"
              style={{
                color: 'var(--dash-text-primary)',
                background: 'var(--dash-hover-bg)',
                border: '1px solid var(--dash-border-solid)',
              }}
            >
              {replaying ? 'Replaying\u2026' : '\u21BB Replay Event'}
            </button>
            {replayMessage && (
              <p
                className={`font-mono text-xs mt-2 ${
                  replayOk === true ? 'text-emerald-400' : replayOk === false ? 'text-red-400' : ''
                }`}
                style={replayOk === null ? { color: 'var(--dash-text-muted)' } : undefined}
              >
                {replayMessage}
              </p>
            )}
            <p className="font-mono text-[10px] mt-2" style={{ color: 'var(--dash-text-faint)' }}>
              Re-runs the handler for this event. Useful after adding a new handler or fixing a bug.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="font-mono text-[10px] uppercase tracking-wider mb-2"
      style={{ color: 'var(--dash-text-muted)' }}
    >
      {children}
    </div>
  );
}

function MetaField({
  label,
  value,
  mono,
  valueClass,
}: {
  label: string;
  value: string;
  mono?: boolean;
  valueClass?: string;
}) {
  return (
    <div>
      <div
        className="font-mono text-[10px] uppercase tracking-wider mb-1"
        style={{ color: 'var(--dash-text-muted)' }}
      >
        {label}
      </div>
      <div
        className={`${mono ? 'font-mono' : 'font-sans'} text-xs ${valueClass || ''}`}
        style={valueClass ? undefined : { color: 'var(--dash-text-secondary)' }}
      >
        {value}
      </div>
    </div>
  );
}
